/**
 * On-device tap diagnostic, for the iOS-Chrome-only "buttons stop responding" report
 * (Asana 1218577055036636, item 1).
 *
 * The bug cannot be reproduced on desktop or in Safari, and iOS Chrome cannot be attached to a
 * remote inspector — so the only way to see what is happening is to put the instrument on the
 * page and read it off the phone.
 *
 * Deliberately written in plain DOM with no React, no imports and no app state. The leading
 * hypothesis is that React itself has stopped responding; a panel rendered by React would freeze
 * alongside it and show nothing. This one keeps logging either way.
 *
 * Off unless switched on. `?__tapdebug=1` arms it for the tab (survives client-side navigation,
 * which the repro depends on — react-router drops the query string when it pushes); `?__tapdebug=0`
 * disarms it. No param, no listeners, no panel, no cost.
 */

const FLAG = '__tapdebug';
const MAX_ROWS = 60;
/** A click that follows its touchend by more than this is not that tap's click. */
const GESTURE_WINDOW_MS = 1_200;
/** How long after a tap we still credit a draft request to it. */
const REACTION_WINDOW_MS = 2_500;

interface Gesture {
  id: number;
  at: number;
  x: number;
  y: number;
  downTarget: string;
  hitTarget: string;
  sameTarget: boolean;
  hasFiber: boolean;
  insideRoot: boolean;
  scrollTop: number;
  clicked: boolean;
  clickTarget: string;
  reacted: boolean;
  reactedAfterMs: number;
}

const describe = (node: Element | null): string => {
  if (!node) {
    return 'none';
  }

  // Tag, test id and the first class only. Never text content — these screens carry the
  // customer's name, email and phone.
  const testId = node.getAttribute?.('data-test');
  const cls = typeof node.className === 'string' ? node.className.split(/\s+/).filter(Boolean)[0] : '';

  return `${node.tagName.toLowerCase()}${testId ? `[${testId}]` : ''}${cls ? `.${cls}` : ''}`;
};

/**
 * React attaches a `__reactFiber$<id>` property to every DOM node it owns. Its absence on a node
 * that is plainly on screen is the signature of the first hypothesis: the visible DOM has come
 * adrift from React, so the browser dispatches a click and nothing is listening.
 */
const hasReactFiber = (node: Element | null): boolean => {
  for (let el = node; el; el = el.parentElement) {
    if (Object.keys(el).some((key) => key.startsWith('__reactFiber$'))) {
      return true;
    }
  }

  return false;
};

const scrollTopOf = (node: Element | null): number => {
  for (let el = node; el; el = el.parentElement) {
    if (el.scrollHeight > el.clientHeight + 1) {
      return Math.round(el.scrollTop);
    }
  }

  return Math.round(window.scrollY);
};

export const isTapDebugEnabled = (search: string, storage: Storage): boolean => {
  try {
    const param = new URLSearchParams(search).get(FLAG);

    if (param === '1') {
      storage.setItem(FLAG, '1');
    }

    if (param === '0') {
      storage.removeItem(FLAG);
    }

    return storage.getItem(FLAG) === '1';
  } catch {
    // Private mode can throw on storage access. A diagnostic must never take the page down.
    return false;
  }
};

export default function initTapDebug(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (!isTapDebugEnabled(window.location.search, window.sessionStorage)) {
    return;
  }

  if ((window as any).__tapDebugRunning) {
    return;
  }

  (window as any).__tapDebugRunning = true;

  const rows: Gesture[] = [];
  let counter = 0;
  let pending: Gesture | null = null;
  let lagMax = 0;

  // ---- panel ----
  //
  // Parked TOP-RIGHT and collapsed to a pill by default, which is not cosmetic. Anchored to the
  // bottom it covered the checkout CTA and the pay button; anchored top-LEFT it would cover
  // "Zurück zur Bestellübersicht", which is the control the other half of this bug is about. The
  // top-right corner is the only area the repro never needs to touch.
  //
  // Everything is pointer-events:none except the pill itself, so the instrument can never be the
  // thing swallowing a tap.
  const panel = document.createElement('div');
  panel.setAttribute('data-test', 'tap-debug');
  panel.style.cssText = [
    'position:fixed',
    'top:0',
    'right:0',
    'z-index:2147483647',
    'display:flex',
    'flex-direction:column',
    'align-items:flex-end',
    'font:9px/1.3 ui-monospace,Menlo,monospace',
    'pointer-events:none',
    'white-space:pre',
  ].join(';');

  const head = document.createElement('div');
  head.setAttribute('data-test', 'tap-debug-pill');
  head.style.cssText = [
    'display:flex',
    'gap:4px',
    'align-items:center',
    'padding:2px 4px',
    'background:rgba(0,0,0,.85)',
    'color:#fff',
    'border-bottom-left-radius:6px',
    'pointer-events:auto',
  ].join(';');

  const toggle = document.createElement('button');
  toggle.textContent = 'tap▼';
  toggle.style.cssText = 'font:9px monospace;pointer-events:auto';

  const copy = document.createElement('button');
  copy.textContent = 'copy';
  copy.style.cssText = 'font:9px monospace;pointer-events:auto';

  const clear = document.createElement('button');
  clear.textContent = 'clear';
  clear.style.cssText = 'font:9px monospace;pointer-events:auto';

  const info = document.createElement('span');

  const body = document.createElement('div');
  body.style.cssText = [
    'display:none',
    'width:100vw',
    'max-height:38vh',
    'overflow:auto',
    'padding:4px 6px',
    'background:rgba(0,0,0,.88)',
    'color:#0f0',
    // The log is never interactive: a tap anywhere over it must reach the page underneath.
    'pointer-events:none',
  ].join(';');

  let expanded = false;

  toggle.addEventListener('click', (event) => {
    event.stopPropagation();
    expanded = !expanded;
    body.style.display = expanded ? 'block' : 'none';
    toggle.textContent = expanded ? 'tap▲' : 'tap▼';
  });

  head.append(toggle, copy, clear, info);
  panel.append(head, body);

  const env = () => {
    const vv = window.visualViewport;

    return [
      `layout ${window.innerWidth}x${window.innerHeight}`,
      vv ? `visual ${Math.round(vv.width)}x${Math.round(vv.height)}@${Math.round(vv.offsetTop)}` : 'visual n/a',
      `scale ${vv ? vv.scale.toFixed(2) : '?'}`,
      `dpr ${window.devicePixelRatio}`,
      `lag ${Math.round(lagMax)}ms`,
    ].join(' · ');
  };

  const render = () => {
    info.textContent = env();
    body.textContent = rows
      .slice(-MAX_ROWS)
      .map((r) => {
        const target = r.sameTarget ? r.downTarget : `${r.downTarget} BUT HIT ${r.hitTarget}`;

        return [
          `#${r.id}`,
          `(${r.x},${r.y})`,
          target,
          `fiber:${r.hasFiber ? 'Y' : 'N'}`,
          `root:${r.insideRoot ? 'Y' : 'N'}`,
          `scroll:${r.scrollTop}`,
          `click:${r.clicked ? r.clickTarget : 'NONE'}`,
          `app:${r.reacted ? `${r.reactedAfterMs}ms` : 'NO'}`,
        ].join(' ');
      })
      .reverse()
      .join('\n');
  };

  copy.addEventListener('click', (event) => {
    event.stopPropagation();
    const text = `${navigator.userAgent}\n${env()}\n${body.textContent ?? ''}`;
    navigator.clipboard?.writeText(text).then(
      () => (copy.textContent = 'copied'),
      () => (copy.textContent = 'copy failed'),
    );
    window.setTimeout(() => (copy.textContent = 'copy'), 1_500);
  });

  clear.addEventListener('click', (event) => {
    event.stopPropagation();
    rows.length = 0;
    render();
  });

  const mount = () => document.body && document.body.appendChild(panel);

  if (document.body) {
    mount();
  } else {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  }

  // ---- main-thread lag: the one measurement that separates "wedged" from "not listening" ----
  let beat = performance.now();
  window.setInterval(() => {
    const now = performance.now();
    lagMax = Math.max(lagMax, now - beat - 200);
    beat = now;
    render();
  }, 200);

  // ---- did the app actually react? a tip tap must produce a draft ----
  const noteReaction = () => {
    const target = rows[rows.length - 1];

    if (target && !target.reacted && performance.now() - target.at < REACTION_WINDOW_MS) {
      target.reacted = true;
      target.reactedAfterMs = Math.round(performance.now() - target.at);
      render();
    }
  };

  const openXhr = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method: string, url: string, ...rest: any[]) {
    if (String(url).includes('order-drafts')) {
      noteReaction();
    }

    return openXhr.call(this, method, url, ...(rest as []));
  };

  const originalFetch = window.fetch;
  window.fetch = function (...args: Parameters<typeof fetch>) {
    if (String((args[0] as Request)?.url ?? args[0]).includes('order-drafts')) {
      noteReaction();
    }

    return originalFetch.apply(this, args);
  };

  // ---- the gesture itself ----
  window.addEventListener(
    'touchend',
    (event: TouchEvent) => {
      try {
        const touch = event.changedTouches[0];

        if (!touch) {
          return;
        }

        const x = Math.round(touch.clientX);
        const y = Math.round(touch.clientY);
        const downTarget = event.target as Element;
        // What the browser says is at those coordinates RIGHT NOW. If this disagrees with the
        // event's own target, the hit-test region and the painted pixels have come apart — the
        // second hypothesis.
        const hit = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null;
        const root = document.getElementById('root');

        pending = {
          id: ++counter,
          at: performance.now(),
          x,
          y,
          downTarget: describe(downTarget),
          hitTarget: describe(hit),
          sameTarget: hit === downTarget || Boolean(hit && downTarget?.contains?.(hit)),
          hasFiber: hasReactFiber(downTarget),
          insideRoot: Boolean(root && downTarget && root.contains(downTarget)),
          scrollTop: scrollTopOf(downTarget),
          clicked: false,
          clickTarget: '',
          reacted: false,
          reactedAfterMs: 0,
        };

        rows.push(pending);

        if (rows.length > MAX_ROWS * 2) {
          rows.splice(0, rows.length - MAX_ROWS);
        }

        render();
      } catch {
        // Never let the instrument break the page it is measuring.
      }
    },
    true,
  );

  window.addEventListener(
    'click',
    (event) => {
      try {
        if (pending && performance.now() - pending.at < GESTURE_WINDOW_MS) {
          pending.clicked = true;
          pending.clickTarget = describe(event.target as Element);
          render();
        }
      } catch {
        // As above.
      }
    },
    true,
  );
}
